import {
  CheckSquare,
  ChevronRight,
  CreditCard,
  Globe,
  Heart,
  Pill,
  Plane,
  Play,
  Shield,
  Star,
  Users,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { DEMO_STEPS, useDemoSteps } from "../../components/demo/DemoNavigator";
import { PageBanner } from "../../components/ui/PageBanner";
import { getCountryName } from "../../data/countries";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";

const SCENARIO_ICONS: Record<string, React.ElementType> = {
  travel: Plane,
  "health-data": Pill,
  checkin: CheckSquare,
  emergency: Shield,
  card: CreditCard,
};

const DemoPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useI18nStore();
  const fmt = useLocaleFormat();
  const steps = useDemoSteps();

  const startDemo = (stepPath?: string) => {
    sessionStorage.setItem("demo-mode", "true");
    navigate(stepPath ?? DEMO_STEPS[0]?.path ?? "/demo");
  };

  const startFullDemo = () => startDemo();

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      <PageBanner
        icon={Play}
        title="HealthPass Demo"
        description={t.demoPageDescription}
        gradient="from-indigo-600 to-blue-800"
      />

      {/* Hero CTA */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white text-center shadow-xl">
        <div className="flex justify-center gap-6 mb-6">
          <div className="flex flex-col items-center gap-1">
            <Globe className="w-8 h-8 opacity-80" />
            <span className="text-xs opacity-70">{t.demoCountriesBadge}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Heart className="w-8 h-8 opacity-80" />
            <span className="text-xs opacity-70">FHIR R5</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Users className="w-8 h-8 opacity-80" />
            <span className="text-xs opacity-70">{t.demoMultilingual}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Shield className="w-8 h-8 opacity-80" />
            <span className="text-xs opacity-70">{t.demoGdpr}</span>
          </div>
        </div>
        <h2 className="text-2xl font-bold mb-3">
          {t.demoTripTitle
            .replace("{from}", getCountryName("DE", language))
            .replace("{to}", getCountryName("TH", language))}
        </h2>
        <p className="text-blue-100 mb-6 max-w-xl mx-auto">
          {t.demoTripDescription.replace(
            "{country}",
            getCountryName("TH", language),
          )}
        </p>
        <button
          onClick={startFullDemo}
          className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-700 font-semibold rounded-xl text-lg hover:bg-blue-50 transition-colors shadow-lg"
        >
          <Play className="w-5 h-5" />
          {t.demoStartButton}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* The five scenarios */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          &nbsp;{t.demoScenariosHeading}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {steps.map((step) => {
            const Icon = SCENARIO_ICONS[step.key] ?? Star;
            return (
              <button
                key={step.id}
                onClick={() => startDemo(step.path)}
                className="flex items-start gap-4 p-5 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all text-left group"
              >
                <div
                  className={`p-3 rounded-xl ${step.color} text-white flex-shrink-0`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {t.demoScenarioLabel.replace("{n}", String(step.id))}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                    {step.title}
                  </h4>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {step.description}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 mt-1 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Test data */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
          <Star className="w-4 h-4" />
          {t.demoTestDataTitle}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {[
            { label: t.demoDataUser, value: "Terry Pazzo" },
            { label: t.demoDataLogin, value: "tp@demo.ll" },
            {
              label: t.demoDataHomeCountry,
              value: getCountryName("DE", language),
            },
            {
              label: t.demoDataDestination,
              value: `${getCountryName("TH", language)} 🇹🇭`,
            },
            {
              label: t.demoDataTravelPeriod,
              value: `${fmt.date("2026-05-15")} – ${fmt.date("2026-05-30")}`,
            },
            { label: t.demoDataMedication, value: "Malarone" },
            { label: t.demoDataVaccination, value: t.demoDataVaccinationValue },
            { label: t.demoDataOperator, value: "Thai Airways" },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-white rounded-lg p-2.5 border border-amber-100"
            >
              <div className="text-xs text-amber-600 font-medium">
                {item.label}
              </div>
              <div className="text-gray-900 font-semibold text-sm">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
